import { sendRequest } from '../services/rest.ts';
import { wsClient } from '../services/web-socket.ts';
import { ReferenceMetadata } from './components/chat-window/chat-window.element.ts';
import { logger } from '../utilities/logger.ts';
import { Native } from '../utilities/native.ts';
import { ConfigService } from '../services/config-service.ts';

let apiBaseUrl: string;
let apiSearchEndpoint: string;

export interface ChatMessageResponse {
  type: 'human' | 'bot';
  content: string;
  metadata?: ReferenceMetadata[];
}

interface ChatSessionResponse {
  chatSession: string;
  title: string;
}

export type QuestionResponse = {
  answer: string;
  chatSession: string;
  runId: string;
  newChat: boolean;
  metadata?: ReferenceMetadata[];
};

export const initialiseApi = async () => {
  if (apiBaseUrl) {
    return apiBaseUrl;
  }

  if (Native.getHostname() === 'localhost') {
    apiBaseUrl = `http://rest.${window.location.host}/latest`;
  } else {
    const json = await ConfigService.configServiceRequest(
      'query GetServiceRegistry { getServiceRegistry(id:"ai_search_url") { url }}',
    );
    if (json.data) {
      // This can be disabled because `apiBaseUrl` is only set once during initialization
      // eslint-disable-next-line require-atomic-updates
      apiBaseUrl = json.data.getServiceRegistry.url;
    } else {
      throw new Error('Error retrieving AI Search endpoint from config service');
    }
  }

  apiSearchEndpoint = `${apiBaseUrl}/ai-search`;
  logger.info(`AI Search endpoint: ${apiSearchEndpoint}`);
  return apiBaseUrl;
};

export const getApiBaseUrl = async () => {
  if (!apiBaseUrl) {
    await initialiseApi();
  }
  return new URL(apiBaseUrl);
};

export function deleteChat(chatSessionId: string): Promise<null | Response> {
  return sendRequest(`${apiSearchEndpoint}/${chatSessionId}`, { method: 'DELETE' }, false);
}

export function getChatHistory(chatSessionId: string): Promise<ChatMessageResponse[]> {
  return sendRequest(`${apiSearchEndpoint}/${chatSessionId}`, { method: 'GET' });
}

export function getChatSessions(): Promise<ChatSessionResponse[]> {
  return sendRequest(apiSearchEndpoint, { method: 'GET' });
}

export function sendFeedback(runId: string, feedbackType: number): Promise<null | Response> {
  return sendRequest(`${apiSearchEndpoint}/feedback/${runId}/${feedbackType}`, { method: 'POST' }, false);
}

export async function sendMessage(
  questionText: string,
  currentChatSessionId: string | null = null,
): Promise<QuestionResponse> {
  const response = await wsClient.sendChatMessage(questionText, currentChatSessionId);
  return {
    answer: response.chatMessageResponse,
    chatSession: response.chatSessionId,
    runId: response.dbxMlflowRunId,
    newChat: response.newChat,
    metadata: response.metadata,
  };
}

if (window.Cypress) {
  initialiseApi();
}
